import React, { ChangeEvent, useCallback, useRef, useState } from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles } from '@material-ui/core/styles';

// ─── Public type (flows into template parameters / ctx.input.envConfig) ───────

/**
 * What lands in template parameters (formData / ctx.input.envConfig).
 * Secret rows carry no `value` — actual values live in ctx.secrets.envSecretValues.
 */
export type EnvConfigEntry = {
  key: string;
  value?: string;
  secret: boolean;
};

// ─── Internal display row ─────────────────────────────────────────────────────

type Row = {
  key: string;
  value: string; // always the real value; never written to formData for secret rows
  secret: boolean;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  tableWrapper: { overflowX: 'auto', marginTop: theme.spacing(1) },
  table: { tableLayout: 'fixed', minWidth: 520 },
  colKey: { width: '40%' },
  colValue: { width: '36%' },
  colSecret: { width: '14%', textAlign: 'center' as const },
  colAction: { width: '10%', textAlign: 'center' as const },
  mono: { fontFamily: 'monospace', fontSize: 13 },
  gsmBadge: {
    display: 'inline-block',
    background: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: '1px 5px',
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginLeft: 4,
    verticalAlign: 'middle',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
    marginTop: theme.spacing(1),
    alignItems: 'center',
  },
  csvHint: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginTop: theme.spacing(0.5),
  },
  hiddenInput: { display: 'none' },
}));

// ─── CSV helpers ──────────────────────────────────────────────────────────────

function parseCsv(text: string): Row[] {
  const lines = text
    .split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith('#'));
  if (lines.length < 2) return [];

  const header = lines[0].split(',').map(h => h.trim().toLowerCase());
  const ki = header.indexOf('key');
  const vi = header.indexOf('value');
  const si = header.indexOf('secret');
  if (ki === -1 || vi === -1) return [];

  return lines
    .slice(1)
    .map(line => {
      const cols = line.split(',');
      return {
        key: cols[ki]?.trim() ?? '',
        value: cols[vi]?.trim() ?? '',
        secret: si !== -1 ? cols[si]?.trim().toLowerCase() === 'true' : false,
      };
    })
    .filter(r => r.key);
}

const CSV_TEMPLATE =
  'key,value,secret\nPORT,8080,false\nLOG_LEVEL,info,false\ndb.connectionstring,,true\nDB_PASSWORD,,true\n';

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'env-config.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Secret values helpers ────────────────────────────────────────────────────

function parseSecretValues(raw: string | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EnvConfigField = ({
  onChange,
  formData,
  rawErrors,
  required,
  schema,
  disabled,
}: FieldExtensionComponentProps<EnvConfigEntry[]>) => {
  const classes = useStyles();
  const { setSecrets, secrets } = useTemplateSecrets();
  const csvRef = useRef<HTMLInputElement>(null);

  // Reconstruct display rows on mount:
  //   non-secret rows → value from formData
  //   secret rows     → value from SecretsContext (survives wizard step navigation)
  const [rows, setRows] = useState<Row[]>(() => {
    const secretValues = parseSecretValues(secrets.envSecretValues);
    return (formData ?? []).map(e => ({
      key: e.key,
      value: e.secret ? secretValues[e.key] ?? '' : e.value ?? '',
      secret: e.secret,
    }));
  });

  // Sync display rows → formData + SecretsContext on every change.
  const sync = useCallback(
    (next: Row[]) => {
      setRows(next);

      // formData: secret rows carry no value (never written to task parameters)
      const entries: EnvConfigEntry[] = next.map(r =>
        r.secret
          ? { key: r.key, secret: true }
          : { key: r.key, value: r.value, secret: false },
      );
      onChange(entries);

      // SecretsContext: JSON map of key → value for secret rows only
      const secretMap: Record<string, string> = {};
      next.forEach(r => {
        if (r.secret) secretMap[r.key] = r.value;
      });
      setSecrets({ envSecretValues: JSON.stringify(secretMap) });
    },
    [onChange, setSecrets],
  );

  const addRow = () => sync([...rows, { key: '', value: '', secret: false }]);

  const removeRow = (i: number) => sync(rows.filter((_, idx) => idx !== i));

  const updateKey = (i: number, key: string) =>
    sync(rows.map((r, idx) => (idx === i ? { ...r, key } : r)));

  const updateValue = (i: number, value: string) =>
    sync(rows.map((r, idx) => (idx === i ? { ...r, value } : r)));

  const updateSecret = (i: number, secret: boolean) =>
    sync(rows.map((r, idx) => (idx === i ? { ...r, secret } : r)));

  const handleCsvUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCsv((ev.target?.result as string) ?? '');
      if (parsed.length) sync([...rows, ...parsed]);
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-upload of same file
  };

  const title = (schema as any)?.title as string | undefined;
  const description = (schema as any)?.description as string | undefined;

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
          {required && <span style={{ color: 'red' }}>&thinsp;*</span>}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {description}
        </Typography>
      )}

      <Box className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.colKey}>Key</TableCell>
              <TableCell className={classes.colValue}>Value</TableCell>
              <TableCell className={classes.colSecret} align="center">
                Secret
              </TableCell>
              <TableCell className={classes.colAction} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="caption" color="textSecondary">
                    No entries — add a row or upload a CSV
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={i}>
                <TableCell className={classes.colKey}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. DB_PASSWORD or db.connectionstring"
                    value={row.key}
                    onChange={e => updateKey(i, e.target.value)}
                    disabled={disabled}
                    inputProps={{ className: classes.mono }}
                  />
                </TableCell>

                <TableCell className={classes.colValue}>
                  <TextField
                    fullWidth
                    size="small"
                    type={row.secret ? 'password' : 'text'}
                    placeholder={row.secret ? '••••••••' : 'value'}
                    value={row.value}
                    onChange={e => updateValue(i, e.target.value)}
                    disabled={disabled}
                    autoComplete="off"
                    inputProps={{ className: classes.mono }}
                    // Show GSM badge when secret so the reviewer knows where it goes
                    InputProps={
                      row.secret
                        ? {
                            endAdornment: (
                              <span className={classes.gsmBadge}>GSM</span>
                            ),
                          }
                        : undefined
                    }
                  />
                </TableCell>

                <TableCell className={classes.colSecret} align="center">
                  <Tooltip
                    title={
                      row.secret
                        ? 'Value stored in Google Secret Manager (not in git or task DB)'
                        : 'Value stored in ConfigMap (plain text, not encrypted)'
                    }
                  >
                    <Checkbox
                      checked={row.secret}
                      onChange={e => updateSecret(i, e.target.checked)}
                      disabled={disabled}
                      size="small"
                      color="secondary"
                    />
                  </Tooltip>
                </TableCell>

                <TableCell className={classes.colAction} align="center">
                  <IconButton
                    size="small"
                    onClick={() => removeRow(i)}
                    disabled={disabled}
                    aria-label="remove row"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box className={classes.actions}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
        >
          Add Row
        </Button>

        <Tooltip title="Upload CSV (columns: key, value, secret). Secret values in the file are sensitive — delete after use.">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => csvRef.current?.click()}
            disabled={disabled}
          >
            Upload CSV
          </Button>
        </Tooltip>

        <Button
          size="small"
          startIcon={<GetAppIcon />}
          onClick={downloadCsvTemplate}
          disabled={disabled}
        >
          Download template CSV
        </Button>

        <input
          ref={csvRef}
          type="file"
          accept=".csv,text/csv"
          className={classes.hiddenInput}
          onChange={handleCsvUpload}
        />
      </Box>

      <Typography className={classes.csvHint}>
        CSV columns: <code>key, value, secret</code> (secret = true/false). Keys
        with dots or underscores are supported (e.g.{' '}
        <code>db.connectionstring</code>). Secret values in CSV are sensitive —
        delete the file after upload.
      </Typography>

      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </Box>
  );
};
